// cSpell:ignore betalen GEBOUW geven laste PRIVAAT terug
// What the cost list and the settlement list below it share: their frame and
// column gap, the header row, the rows, and the cells their titles, dates,
// periods, lozenges and amounts are written in. Both tables use these
// components, so their headers (font, size, weight, color, padding, divider),
// their rows (height, padding, divider, hover) and their cells look the same
// and cannot drift apart.

import { FontWeights } from "@rentiohq/web-theme";
import styled from "styled-components";
import { moneyFont } from "./Money";

// The space between two columns, in the header and in the rows under it: the
// header's cells only line up with the rows' cells when both use it.
export const LIST_COLUMN_GAP = 12;

// The rows' inner space left and right, where a hovered row's background
// shows.
export const LIST_SIDE_PADDING = 8;

export const listSpread = (spread?: boolean) =>
  spread ? "justify-content: space-between;" : "";

// Around a table: it reaches LIST_SIDE_PADDING beyond the card's content on
// both sides, so the first column's text lines up with the card's title and
// the search field above it, and the row menu with the buttons.
export const ListFrame = styled.div`
  margin: 0 -${LIST_SIDE_PADDING}px;
`;

// `$spread`: columns that all have a largest width share the room a wide
// screen leaves as even extra space between them; the rows under the header
// must spread the same way (listSpread). Its text and divider are the
// dashboard's table header's (DataTable's Header).
export const ListHeaderRow = styled.div<{
  $columns: string;
  $spread?: boolean;
}>`
  display: grid;
  grid-template-columns: ${({ $columns }) => $columns};
  gap: ${LIST_COLUMN_GAP}px;
  ${({ $spread }) => listSpread($spread)}
  padding: 8px ${LIST_SIDE_PADDING}px;
  font-size: 14px;
  font-weight: ${FontWeights.SemiBold};
  border-bottom: 2px solid ${({ theme }) => theme.colors.neutral30};
`;

// A cell that takes `span` of the grid's columns (the settlement list's
// period takes the cost list's "Ten laste" too); one by default.
const gridSpan = (span?: number) =>
  span && span > 1 ? `grid-column: span ${span};` : "";

export const ListHeaderCell = styled.div<{
  $align?: "left" | "center" | "right";
  $span?: number;
}>`
  min-width: 0;
  line-height: 1.2;
  text-align: ${({ $align }) => $align ?? "left"};
  ${({ $span }) => gridSpan($span)}
  /* A centered header stays on one line ("Ten laste" over a lozenge): a
  word or two wider than its narrow column reaches into the gaps on both
  sides instead of wrapping. */
  ${({ $align }) => ($align === "center" ? "white-space: nowrap;" : "")}
`;

// A date or a period ("01/01/2026 → 30/06/2026") in a row: plain text that,
// when its column is narrow, breaks after the period's arrow only, the second
// day going to its own line ("01/01/2026 →" over "30/06/2026"): each piece
// (periodRangeLines) sits in a `.nowrap` span.
export const ListCellText = styled.div`
  min-width: 0;
  line-height: 1.2;
  .nowrap {
    white-space: nowrap;
  }
`;

// A row: its own grid on the header's columns (`$columns`), spread the same
// way (listSpread), so its cells line up under the header's. Its height comes
// from its padding and its cells, never from a width.
export const ListRow = styled.div<{ $columns: string }>`
  display: grid;
  grid-template-columns: ${({ $columns }) => $columns};
  gap: ${LIST_COLUMN_GAP}px;
  ${listSpread(true)}
  align-items: center;
  padding: 10px ${LIST_SIDE_PADDING}px;
  border-bottom: 1px solid ${({ theme }) => theme.getColor("gray", 200)};
  cursor: pointer;
  &:hover {
    background: ${({ theme }) => theme.getColor("gray", 0)};
  }
`;

// A cell placed as its column's header is.
export const ListAligned = styled.div<{
  $align: "left" | "center" | "right";
  $span?: number;
}>`
  min-width: 0;
  text-align: ${({ $align }) => $align};
  ${({ $span }) => gridSpan($span)}
`;

export const ListCell = styled.div`
  min-width: 0;
  line-height: 1.3;
`;

// Text that matters less than a row's title (who bears a cost, a dash).
export const ListSecondary = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.getColor("gray", 800)};
`;

// One line, cut short with an ellipsis (the whole text in its tooltip).
export const ListOneLine = styled(ListSecondary)`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

// The small line under a cell's text: a cost's supplier, a settlement's
// description, the day under a status.
export const ListSubLine = styled(ListOneLine)`
  margin-top: 2px;
  font-size: 12px;
`;

// The same small line, wrapping on a narrow screen instead of being cut: a
// settlement's "1 te betalen · 3 terug te geven", which says what is left.
export const ListSubText = styled(ListSecondary)`
  margin-top: 2px;
  font-size: 12px;
  .part {
    white-space: nowrap;
  }
`;

// A row's title (a cost's, a settlement's) on one line, cut short with an
// ellipsis.
export const ListTitle = styled.div`
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: ${({ theme }) => theme.getColor("gray", 1000)};
`;

// A lozenge (the status, the type), cut short when the column is narrow (the
// whole text in its tooltip), with its lines under it. The lozenge keeps its
// column's alignment.
export const ListLozengeBox = styled.div`
  min-width: 0;
  line-height: 1.2;
  /* The tooltip's trigger, then the lozenge: each no wider than the column. */
  > :first-child,
  .lozenge {
    max-width: 100%;
  }
  .lozenge {
    display: block;
  }
  .lozenge > span {
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    vertical-align: top;
  }
`;

// The Type cell's lozenge ("GEBOUW", "PRIVAAT"), with or without the
// tooltip that names a unit's address: one line exactly as high as the
// lozenge either way (no line box around it, the tooltip's trigger adds
// nothing), so a row with the tooltip is as high as one without. The lozenge
// keeps its column's alignment and is cut short when the column is narrow.
export const ListTypeBox = styled.div`
  min-width: 0;
  line-height: 0;
  > *,
  .lozenge {
    display: inline-flex;
    max-width: 100%;
    vertical-align: top;
  }
  .lozenge > span {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

// A cost's amount, left-aligned as its column is, with its small lines under
// it ("+ € 21,00 btw"), both in the financial font (Money); a share still to
// be worked out says so in words.
export const ListAmount = styled.div`
  text-align: left;
  font-variant-numeric: tabular-nums;
  line-height: 1.3;
  .amount {
    font-weight: ${FontWeights.Regular};
    color: ${({ theme }) => theme.getColor("gray", 1000)};
    ${moneyFont}
  }
  .pending {
    font-size: 13px;
    color: ${({ theme }) => theme.getColor("gray", 800)};
  }
  .sub {
    font-size: 12px;
    color: ${({ theme }) => theme.getColor("gray", 800)};
    margin-top: 2px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    ${moneyFont}
  }
`;

// Text only a screen reader reads, such as the tooltip that explains an
// amount.
export const ListScreenReaderText = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
`;

// The row menu's trigger: a real button, so the menu can be reached and
// opened by keyboard.
export const ListMenuButton = styled.button`
  cursor: pointer;
  color: ${({ theme }) => theme.getColor("gray", 600)};
  justify-self: end;
  padding: 0;
  border: 0;
  background: none;
  font: inherit;
`;

// The filters above a list (the costs' and the settlements'), outside the
// table so they stay in view when the grid scrolls sideways.
export const ListToolBar = styled.div`
  padding-bottom: 12px;
`;

// Search bar + the dropdown filters on one row; the search stretches, the
// selects keep a comfortable width. On a narrow screen the row wraps: the
// search takes its own line and the selects go below it, instead of the
// selects squeezing the search to nothing.
export const ListFilterRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 12px;

  .search {
    flex: 2 1 220px;
    min-width: 200px;
  }
  .select {
    flex: 1 1 140px;
    max-width: 220px;
  }
`;
